import { Checkbox } from "@mui/material";
import classNames from "classnames";
import { isValid } from "date-fns";
import { useState } from "react";
import useTranslation from "../../hooks/useTranslation";
import SurveyDispatch from "../../models/surveyDispatch";
import { SurveyDispatchReminder } from "../../types/survey";
import {
  getLastBusinessDayBeforeDueDate,
  getReminderMaxDate,
  getReminderMinDate,
  sortReminders,
} from "../../utils/survey.utils";
import { Button } from "../ui/Button/Button";
import { Card } from "../ui/Card/Card";
import { Modal } from "../ui/Modal/Modal";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import { Typography } from "../ui/Typography/Typography";
import { TrashIcon } from "../ui/icons/TrashIcon";
import { WarningIcon } from "../ui/icons/WarningIcon";
import { DateType } from "./SurveySettings";
import { SurveySettingsDatePicker } from "./SurveySettingsDatePicker";
import "./SurveySettings.scss";

interface SurveySettingsReminderProps {
  dispatch: SurveyDispatch;
  reminders: SurveyDispatchReminder[];
  launchDate: Date | undefined;
  dueDate: Date | undefined;
  setReminders: (reminders: SurveyDispatchReminder[]) => void;
  setIsChanged: (isChanged: boolean) => void;
  setIsError: (isError: boolean) => void;
  setSubmitError: (error: string) => void;
}

export const SurveySettingsReminder = ({
  dispatch,
  reminders,
  launchDate,
  dueDate,
  setReminders,
  setIsChanged,
  setIsError,
  setSubmitError,
}: SurveySettingsReminderProps) => {
  const { t } = useTranslation("surveyLaunchPage");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const finalReminderIndex = reminders.findIndex(
    (reminder) => reminder.isFinal
  );
  const finalReminder = reminders[finalReminderIndex];
  const maxReminders = 10;

  const handleAddReminder = () => {
    reminders.length < maxReminders &&
      setReminders(
        sortReminders([
          ...reminders,
          { id: null, tempId: Date.now(), date: undefined, isFinal: false },
        ])
      );
  };

  const handleClick = (index: number, reminder: SurveyDispatchReminder) => {
    if (reminder.isFinal) {
      setIsModalOpen(true);
    } else {
      handleDeleteReminder(index);
    }
  };

  const handleDeleteReminder = (index: number) => {
    const newReminders = reminders.filter((_, i) => i !== index);
    setReminders(newReminders);
    setIsError(false);
    setSubmitError("");
    setIsChanged(true);
  };

  const handleChangeReminderDate = (index: number, date: Date | undefined) => {
    const newReminders = [...reminders];
    newReminders[index] = { ...newReminders[index], date };

    setReminders(sortReminders(newReminders));
    setIsChanged(true);
  };

  const handleChangeReminderStatus = (index: number, isFinal: boolean) => {
    let reminderDate = reminders[index].date;
    if (dueDate && !reminderDate) {
      reminderDate = getLastBusinessDayBeforeDueDate(dueDate);
    }
    const newReminders = [...reminders];
    newReminders[index] = {
      ...newReminders[index],
      date: reminderDate,
      isFinal: isFinal,
    };
    setReminders(sortReminders(newReminders));
    setIsChanged(true);
  };

  return (
    <div className="SurveySettingsReminder">
      {reminders.map((reminder, index) => {
        const isDisabled = reminder.id
          ? dispatch.isReminderDisabled(reminder.id)
          : false;
        const datePickerClasses = classNames(
          "SurveySettingsReminder__inputContainer__datePicker",
          {
            "SurveySettingsReminder__inputContainer__datePicker--final":
              reminder.isFinal,
          }
        );
        return (
          <div
            className="SurveySettingsReminder__inputContainer"
            key={`${reminder.id ?? reminder.tempId}`}
          >
            <Typography
              tagVariant="p"
              desktop="body2"
              className="SurveySettingsReminder__label"
            >
              {index + 1}
            </Typography>
            <SurveySettingsDatePicker
              className={datePickerClasses}
              dataTestId={`reminderDate${index}`}
              value={reminder.date}
              setValue={(date) => handleChangeReminderDate(index, date)}
              type={DateType.REMINDER}
              disabled={isDisabled}
              minDate={getReminderMinDate(launchDate, reminders, reminder)}
              maxDate={getReminderMaxDate(dueDate, reminder, finalReminder)}
              setIsChanged={setIsChanged}
              setIsError={setIsError}
            />
            {index === reminders.length - 1 && (
              <Tooltip title={t("edit.reminders.addFinalInfo")}>
                <div className="SurveySettingsReminder__checkboxContainer">
                  <Checkbox
                    data-testid="finalReminderCheckbox"
                    checked={reminder.isFinal}
                    onChange={(e) =>
                      handleChangeReminderStatus(index, e.target.checked)
                    }
                  />
                  <Typography desktop="body2">
                    {t("edit.reminders.final")}
                  </Typography>
                </div>
              </Tooltip>
            )}
            <Tooltip title={t("edit.reminders.delete")}>
              <Button
                variant="contained"
                color="secondary"
                icon
                onClick={() => handleClick(index, reminder)}
                small
                sx={{ visibility: isDisabled ? "hidden" : "visible" }}
                data-testid={`deleteReminder${
                  reminder.isFinal ? "Final" : index
                }`}
              >
                <TrashIcon />
              </Button>
            </Tooltip>
          </div>
        );
      })}

      {reminders.length < maxReminders && (
        <Card
          className="SurveySettingsReminder__addReminder"
          inactive
          onClick={handleAddReminder}
          disabled={!isValid(dueDate)}
          data-testid="addReminder"
        >
          + {t("edit.reminders.add")}
        </Card>
      )}
      {reminders.length >= maxReminders && (
        <div className="SurveySettingsReminder__info">
          <Typography tagVariant="p" desktop="body2">
            {t("edit.reminders.maxNumber")}
          </Typography>
        </div>
      )}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          <div className="SurveySettingsReminder__modal__header">
            <WarningIcon />
            <Typography desktop="h3">{t("edit.reminders.delete")}</Typography>
          </div>
        }
      >
        <div>
          <Typography
            desktop="body1"
            className="SurveySettingsReminder__modal__text"
          >
            {t("edit.reminders.confirmation.msg")}
          </Typography>
          <div className="SurveySettingsReminder__modal__actions">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              {t("edit.reminders.confirmation.no")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleDeleteReminder(finalReminderIndex);
                setIsModalOpen(false);
              }}
              data-testid="confirmDeleteReminder"
            >
              {t("edit.reminders.confirmation.yes")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
